import { router } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { SegmentedControl } from "@/components/segmented-control"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { brandAnalyticsChannelPath } from "@/routes"
import {
  CHANNEL_METRIC_VALUES,
  CHANNEL_RELOAD_ONLY,
  CHANNEL_WINDOW_VALUES,
  type ChannelMetric,
  type ChannelWindow,
} from "@/types/channel-analytics"

/**
 * OKS-734 — URL-stateful filter bar for the Channel analytics dashboard.
 *
 * Two controls (same partial-reload idiom as `analytics-filter-bar`,
 * OKS-63):
 *   - `SegmentedControl` for the time `window` (7d / 30d / 90d).
 *   - shadcn `Select` for the chart `metric` (Followers / Following /
 *     Likes / Videos) — plain shadcn + `name` attr, NOT shadcn `Form`.
 *
 * A change fires `router.get(brandAnalyticsChannelPath({...}), undefined,
 * { only: CHANNEL_RELOAD_ONLY, preserveState: false, preserveScroll: true })`.
 * The URL is the only state — no `localStorage` (PRD §6 + Crew hard rule);
 * refresh / back preserve via Inertia's URL-driven page resolution. The
 * current video sort/dir/page are carried through so a window/metric change
 * doesn't reset the table.
 */
export interface ChannelAnalyticsFilterBarProps {
  window: ChannelWindow
  metric: ChannelMetric
  /** Current table sort — carried through so it survives a filter change. */
  sort: string
  dir: string
}

export function ChannelAnalyticsFilterBar({
  window,
  metric,
  sort,
  dir,
}: ChannelAnalyticsFilterBarProps) {
  const { t } = useTranslation()

  const apply = (next: { window?: ChannelWindow; metric?: ChannelMetric }) => {
    router.get(
      brandAnalyticsChannelPath({
        window: next.window ?? window,
        metric: next.metric ?? metric,
        sort,
        dir,
      }),
      undefined,
      {
        only: [...CHANNEL_RELOAD_ONLY],
        preserveState: false,
        preserveScroll: true,
      },
    )
  }

  return (
    <section
      aria-label={t("channel.filters.section_aria")}
      data-testid="channel-analytics-filter-bar"
      className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4"
    >
      <div className="flex flex-col gap-1.5">
        <Label className="text-muted-foreground text-xs font-medium">
          {t("channel.filters.window.label")}
        </Label>
        <SegmentedControl
          value={window}
          onValueChange={(v: ChannelWindow) => apply({ window: v })}
          variant="outline"
          size="sm"
          data-testid="channel-analytics-window"
          aria-label={t("channel.filters.window.label")}
          options={CHANNEL_WINDOW_VALUES.map((value) => ({
            value,
            label: t(`channel.filters.window.${value}`),
            "data-testid": `channel-analytics-window-${value}`,
            "aria-label": t(`channel.filters.window.${value}`),
          }))}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label
          htmlFor="channel-analytics-metric-select"
          className="text-muted-foreground text-xs font-medium"
        >
          {t("channel.filters.metric.label")}
        </Label>
        <Select
          items={CHANNEL_METRIC_VALUES.map((value) => ({
            value,
            label: t(`channel.filters.metric.${value}`),
          }))}
          value={metric}
          onValueChange={(v) => v != null && apply({ metric: v })}
          name="metric"
        >
          <SelectTrigger
            id="channel-analytics-metric-select"
            data-testid="channel-analytics-metric-select"
            className="min-w-[180px]"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CHANNEL_METRIC_VALUES.map((value) => (
              <SelectItem key={value} value={value}>
                {t(`channel.filters.metric.${value}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </section>
  )
}
